// src/encounter/lib/to-growth-series.ts
// Map the longitudinal vitals history (GET /api/Patients/GetVitalsHistoryByCode, one PatientVital[]
// per LOINC) → the DS `GrowthSeries` the `GrowthChart` plots. PROD-1324, reworked for Flutter parity
// under PROD-2044.
//
// A GrowthSeries is Partial<Record<GrowthMetric, GrowthObservation[]>>, GrowthMetric =
// length | weight | head | bmi. Each GrowthObservation is { ageMonths, value, loinc?,
// effectiveDateTime? } where:
//   • ageMonths = (reading timestamp − patient birthDate) in fractional months, so the x axis is
//                 age AT the reading, not "now".
//   • value     = the reading magnitude in the metric's canonical unit (kg / cm). The wire ships it
//                 as a stringified number (VitalQuantity.value); the DS converts for display when
//                 the clinician switches the chart to imperial.
//
// TWO CHANGES FROM THE FIRST VERSION, both parity fixes (PROD-2044):
//
//   1. A NEGATIVE AGE IS NO LONGER DROPPED. This file used to return null whenever a reading's
//      timestamp preceded the birth date, which silently discarded the point. On dev, patient
//      Arfaan Shaik (b. 2025-01-02) carries 8 height and 35 weight readings all timestamped in
//      2023 — so React drew an empty chart over 43 real rows while Flutter plotted every one of
//      them at a negative age. Flutter's 0-2 window is `<= 730 days` with NO lower bound
//      (`growth_chart.dart:899`). The data is a dev artifact, but a chart that cannot distinguish
//      "never measured" from "measured and rejected" is the worse failure. The DS applies the
//      per-dataset window; this file's job is to stop throwing the rows away.
//
//   2. THE AGE GATE IS FLUTTER'S. `isGrowthChartAge` was `age < 2`, because the DS chart only drew
//      the WHO 0-24mo domain and its `standard` prop never reached the scale math. So no growth
//      chart rendered for ANY patient aged 2-19 — confirmed live with a 16-year-old on dev. The DS
//      now ships all three of Flutter's domains, and the gate is Flutter's: age <= 19
//      (`growth_chart.dart:1218`). The DS re-derives the domain list from `ageYears` itself, so this
//      predicate only decides whether to FETCH.
import type {
  GrowthSeries,
  GrowthObservation,
  GrowthMetric,
  GrowthLengthWeightPoint,
} from '@scripsteam/scrips-signal-ds';
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';

/**
 * Is this patient in range for a growth chart at all? Flutter renders `GrowthChartGraph` only when
 * `getAgeFromText(patient.age) <= 19` (`growth_chart.dart:1218`) and returns an empty Container
 * otherwise. Used here to gate the four history FETCHES; the DS decides which domains to offer.
 */
export function isGrowthChartAge(ageYears: number | null | undefined): boolean {
  return ageYears != null && Number.isFinite(ageYears) && ageYears >= 0 && ageYears <= 19;
}

/**
 * Map demographics sex → the DS chart's sex reference. Returns `undefined` for a missing or
 * non-binary/'X' sex so the caller withholds the chart: the DS renders nothing without a sex rather
 * than defaulting, because girls' percentiles shown for a boy is a wrong chart, not a cosmetic one.
 */
export function growthSexFrom(sex: 'M' | 'F' | 'X' | null | undefined): 'male' | 'female' | undefined {
  return sex === 'F' ? 'female' : sex === 'M' ? 'male' : undefined;
}

/** The growth metrics the chart plots, each bound to the LOINC its history is fetched by. */
export const GROWTH_METRIC_CODES: Record<GrowthMetric, string> = {
  length: VITAL_CODES.HEIGHT, // 8302-2 — length (infant) and stature (child) share this code
  weight: VITAL_CODES.WEIGHT, // 29463-7
  head: VITAL_CODES.HCM, // 11947-9, as Flutter saves and reads it (growth_chart.dart:273; PROD-3185)
  bmi: VITAL_CODES.BMI, // 39156-5 (Scrips-local sentinel)
};

const MS_PER_MONTH = 30.4375 * 24 * 60 * 60 * 1000; // Flutter's mean-month divisor (`:477`).

/**
 * Fractional months between two ISO timestamps, or null if either is missing or unparseable.
 *
 * MAY RETURN A NEGATIVE NUMBER — see note 1 in the file header. It used to return null for
 * `to < from`, which is what dropped 43 readings on a real dev patient.
 */
export function monthsBetween(fromIso: string | null | undefined, toIso: string | null | undefined): number | null {
  if (!fromIso || !toIso) return null;
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return (to - from) / MS_PER_MONTH;
}

function readingValue(v: PatientVital): number | null {
  const raw = v.quantity?.value;
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * When a reading happened, for the growth chart: `createdDate`, as Flutter plots it
 * (`growth_chart.dart:458`, and the same in every `generatePatientGraphData*`). Andrew, 9 Oct
 * (PROD-3185): the growth chart follows Flutter exactly. This replaces an earlier preference for
 * `effectiveDateTime`. The cost, accepted: a back-entered reading plots at the age it was written,
 * not the age it was measured. `effectiveDateTime` is used only when a row has no (or an empty) `createdDate`.
 */
function readingWhen(v: PatientVital): string | null {
  // `||`, not `??`: an empty createdDate must fall through to the effective date (Codex, 9 Oct).
  return v.createdDate || v.effectiveDateTime || null;
}

/** One metric's PatientVital[] → GrowthObservation[] (age-for-value points), dropping unusable rows. */
function toObservations(vitals: PatientVital[] | undefined, dob: string | undefined, loinc: string): GrowthObservation[] {
  if (!vitals || !dob) return [];
  const out: GrowthObservation[] = [];
  for (const v of vitals) {
    if (v.isDeleted) continue;
    const when = readingWhen(v);
    const ageMonths = monthsBetween(dob, when);
    const value = readingValue(v);
    if (ageMonths === null || value === null) continue;
    out.push({ ageMonths, value, loinc, effectiveDateTime: when ?? undefined });
  }
  // Chronological by age so the plotted line reads left→right.
  return out.sort((a, b) => a.ageMonths - b.ageMonths);
}

/**
 * Build the DS `GrowthSeries` from the per-LOINC vitals history + the patient's birthDate.
 * `byCode` is keyed by LOINC (what GetVitalsHistoryByCode is queried by). A metric with no readings
 * is omitted (GrowthSeries is Partial) so the chart shows it as empty rather than a zero point.
 */
export function toGrowthSeries(
  byCode: Partial<Record<string, PatientVital[]>>,
  dob: string | undefined,
): GrowthSeries {
  const series: GrowthSeries = {};
  (Object.keys(GROWTH_METRIC_CODES) as GrowthMetric[]).forEach((metric) => {
    const code = GROWTH_METRIC_CODES[metric];
    const obs = toObservations(byCode[code], dob, code);
    if (obs.length) series[metric] = obs;
  });
  return series;
}

/** How close two readings must be to count as the same visit when no encounter id links them. */
const SAME_VISIT_HOURS = 24;

/**
 * Pair length and weight readings into weight-for-length points (the DS "L to W" / "S to W" charts).
 *
 * WHY THIS IS EXPLICIT RATHER THAN INDEX-BASED: Flutter pairs the two lists BY ORDINAL — reading i
 * of the length list against reading i of the weight list (`growth_chart.dart:504-518`) — and
 * bounds the loop by the length list. Whenever the two lists differ in size, and they routinely do
 * (Arfaan Shaik: 8 heights against 35 weights), that plots the weight from one visit against the
 * length from a different one. The result is a chart of measurements that never coexisted, and it
 * is what made Flutter's live "L to W" render a zig-zag.
 *
 * So we pair on what actually links two readings: the same `encounter` first, then a timestamp
 * within a day. A length with no matching weight is dropped rather than paired with the nearest
 * available number — a fabricated point is worse than a missing one.
 */
export function toWeightForLength(
  byCode: Partial<Record<string, PatientVital[]>>,
): GrowthLengthWeightPoint[] {
  const lengths = (byCode[GROWTH_METRIC_CODES.length] ?? []).filter((v) => !v.isDeleted);
  const weights = (byCode[GROWTH_METRIC_CODES.weight] ?? []).filter((v) => !v.isDeleted);
  if (!lengths.length || !weights.length) return [];

  const byEncounter = new Map<string, PatientVital>();
  for (const w of weights) if (w.encounter) byEncounter.set(w.encounter, w);

  const out: GrowthLengthWeightPoint[] = [];
  const used = new Set<PatientVital>();

  for (const l of lengths) {
    const lengthCm = readingValue(l);
    if (lengthCm === null) continue;

    let match = l.encounter ? byEncounter.get(l.encounter) : undefined;
    if (match && used.has(match)) match = undefined;

    if (!match) {
      // No shared encounter — fall back to the nearest so-far-unused weight within a day.
      const lWhen = readingWhen(l);
      const lt = lWhen ? Date.parse(lWhen) : Number.NaN;
      if (!Number.isNaN(lt)) {
        let best: PatientVital | undefined;
        let bestGap = Infinity;
        for (const w of weights) {
          if (used.has(w)) continue;
          const wWhen = readingWhen(w);
          const wt = wWhen ? Date.parse(wWhen) : Number.NaN;
          if (Number.isNaN(wt)) continue;
          const gap = Math.abs(wt - lt);
          if (gap < bestGap) {
            bestGap = gap;
            best = w;
          }
        }
        if (best && bestGap <= SAME_VISIT_HOURS * 3600 * 1000) match = best;
      }
    }

    if (!match) continue;
    const weightKg = readingValue(match);
    if (weightKg === null) continue;
    used.add(match);
    out.push({ length: lengthCm, weight: weightKg, effectiveDateTime: readingWhen(l) ?? undefined });
  }

  return out.sort((a, b) => a.length - b.length);
}
